import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useInvitationContext } from '../../../contexts/InvitationContext';
import InvitationUserRegistration from '../../items/Invitation/InvitationUserRegistration';
import UserLogin from '../../items/Account/UserLogin';
import styles from '../../../styles/LoginRegister.module.css';
import type { Invite } from '../../../types/User';
import type { PostAuthDestination } from '../../../utils/paymentRouting';

interface AcceptInviteProps {
    onLogin: () => Promise<PostAuthDestination>;
}

const AcceptInvite: React.FC<AcceptInviteProps> = ({ onLogin }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [invite, setInvite] = useState<Invite | null>(null);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showLogin, setShowLogin] = useState(false);
    const token = searchParams.get('token');
    
    const { fetchInvitation } = useInvitationContext();

    useEffect(() => {
        const loadInvite = async () => {
            if (!token) {
                //console.error("No invitation token provided");
                setError('No invitation token provided.');
                setLoading(false);
                return;
            }
            
            setLoading(true);
            setError('');
            
            const result = await fetchInvitation(token);
            
            if (result.success && result.invitation) {
                setInvite(result.invitation);
            } else {
                setError(result.error || 'Invalid or expired invitation.');
            }
            
            setLoading(false);
        };
        
        loadInvite();
    }, [token, fetchInvitation]);

    // Handle successful registration and invitation acceptance
    const handleRegistrationSuccess = (message: string) => {
        setSuccessMessage(message);
        
        // Check if user is already logged in (token was stored during registration)
        const token = sessionStorage.getItem('token');
        if (token) {
            // User is automatically logged in, redirect to account
            setTimeout(async () => {
                const destination = await onLogin();
                navigate(destination ?? '/Account');
            }, 2000); // Show success message for 2 seconds before redirecting
        } else {
            // Show login form
            setShowLogin(true);
        }
    };

    // Handle successful login after registration
    const handleLoginSuccess = async () => {
        const destination = await onLogin();
        navigate(destination ?? '/Account');
    };


            if (loading) return (
                <div className={styles.container}>
                    <div className={styles.formSection}>
                        <h2>Accept Invitation</h2>
                        <div className={styles.form}>Loading...</div>
                    </div>
                </div>
            );

            // Show error if invite is not found or invalid
            if (!invite) {
                return (
                    <div className={styles.container}>
                        <div className={styles.formSection}>
                            <h2>Accept Invitation</h2>
                            <div className={styles.form}>
                                <div className={styles.errorMessage}>
                                    {error || 'Invalid or expired invitation.'}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }

    return (
        <div className={styles.container}>
            <div className={styles.formSection}>
                <h2>Accept Invitation</h2>
                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                    Complete your registration below to create your account and automatically accept the invitation.
                </p>
                <div className={styles.form}>
                    <div className={styles.infoRow}>
                        <label className={styles.infoLabel}>Agency:</label>
                        <span className={styles.infoValue}>
                            {invite.agency}
                        </span>
                    </div>
                    <div className={styles.infoRow}>
                        <label className={styles.infoLabel}>Role:</label>
                        <span className={styles.infoValue}>
                            {typeof invite.role === 'string' ? invite.role : invite.role}
                        </span>
                    </div>
                    <div className={styles.infoRow}>
                        <label className={styles.infoLabel}>Email:</label>
                        <span className={styles.infoValue}>
                            {invite.recipientEmail}
                        </span>
                    </div>
                </div>

                {!successMessage ? (
                    <InvitationUserRegistration
                        onSuccess={handleRegistrationSuccess}
                        email={invite.recipientEmail}
                        invitationToken={token || ''}
                    />
                ) : (
                    <div>
                        <p style={{ color: '#22c55e', fontWeight: 500, marginBottom: '16px' }}>
                            {successMessage}
                        </p>
                        
                        {showLogin ? (
                            <>
                                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                                    Please login with your new account to access the system.
                                </p>
                                
                                <UserLogin 
                                    email={invite.recipientEmail}
                                    onLogin={handleLoginSuccess}
                                />
                            </>
                        ) : (
                            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                                Redirecting you to your account...
                            </p>
                        )}
                    </div>
                )}
                {error && <div className={styles.errorMessage}>{error}</div>}
            </div>
        </div>
    );
};

export default AcceptInvite;
